/**
 * Добавление стены с разрезом соседних — через все слои сразу.
 *
 * Новая стена, упёршаяся в середину существующей, режет её узлом на две новые
 * (`splitWallAt`, Т-стык). Всё, что было привязано к старой стене, — розетки, картины,
 * отделка — должно переехать на половинки, иначе потеряет свою стену и пропадёт.
 * Поэтому операция получает полный снимок слоёв, а не одни стены, и возвращает
 * в сторы всё, что изменилось.
 */
import {
	addWallSegment,
	findWallConflict,
	reconcilePlanStages,
	WALL_DEFAULTS,
	WALL_EDGE_TOLERANCE,
} from "@/shared/plan";
import { insetPolygon } from "@/shared/lib/geometry";
import { WallMaterial } from "@/shared/catalog";
import type { Pt } from "@/shared/lib/geometry";
import { getAllLayers } from "../projectStore/helpers";
import type { NewWallOptions } from "../projectStore/projectStore";
import { useWallsStore } from "../layers/wallsStore";
import { useFixturesStore } from "../layers/fixturesStore";
import { useFurnitureFixturesStore, usePlumbingStore } from "../layers/plumbingStore";
import { useWallFinishStore } from "../layers/wallFinishStore";

/** Добавить стену; возвращает id конечного узла — от него рисуется следующая. */
export const addWall = (from: Pt, to: Pt, options?: NewWallOptions): string | null => {
	const before = getAllLayers();
	const { layers, endNodeId } = addWallSegment(before, from, to, {
		thickness: options?.thickness ?? WALL_DEFAULTS.thickness,
		height: options?.height ?? WALL_DEFAULTS.height,
		material: options?.material ?? WallMaterial.Brick,
		bearing: options?.bearing ?? false,
		mounted: options?.mounted ?? false,
		nodeTolerance: options?.snapTolerance ?? 150,
		edgeTolerance: WALL_EDGE_TOLERANCE,
	});

	const rawGraph = {
		nodes: layers.walls.nodes,
		walls: layers.walls.walls,
		openings: layers.walls.openings,
	};
	const reconciled = reconcilePlanStages({ ...layers.walls, ...rawGraph });
	const graph = {
		nodes: reconciled.nodes,
		walls: reconciled.walls,
		openings: reconciled.openings,
	};
	const current = useWallsStore.getState().items;
	if (JSON.stringify(graph) === JSON.stringify(current)) return endNodeId;

	// Сначала объекты и отделка, потом стены: к моменту перерисовки по новым стенам
	// всё уже висит на половинках
	if (layers.electrical.fixtures !== before.electrical.fixtures) {
		useFixturesStore.getState().restoreFixtures(layers.electrical.fixtures);
	}
	if (layers.plumbing.fixtures !== before.plumbing.fixtures) {
		usePlumbingStore.getState().restoreFixtures(layers.plumbing.fixtures);
	}
	if (layers.furniture.fixtures !== before.furniture.fixtures) {
		useFurnitureFixturesStore.getState().restoreFixtures(layers.furniture.fixtures);
	}
	if (layers.finishes.items !== before.finishes.items) {
		useWallFinishStore.getState().restoreFinishes(layers.finishes.items);
	}
	useWallsStore.getState().restoreWalls(graph);
	return endNodeId;
};

/**
 * Замкнутый отдельный контур стен сдвинуть наружу на половину толщины каждой стены:
 * начерченная линия становится внутренней гранью комнаты. Так нарисовал прямоугольник
 * 8 × 2 м — получил комнату 8,00 × 2,00 в чистоте, а не 7,88 × 1,88 (линия черчения — ось
 * стены, и полстены уходило внутрь комнаты).
 *
 * Только для отдельного контура: в каждом его узле ровно две стены, ничего не примыкает.
 * Примыкает перегородка или контур — часть большого плана — где «внутри», непонятно,
 * и стены остаются, как начерчены. Не трогаем и тогда, когда раздвинутые стены задели бы
 * соседние: стены не пересекаются и не накладываются. Возвращает true, если сдвинули.
 */
export const inflateClosedContour = (startNodeId: string): boolean => {
	const graph = useWallsStore.getState().items;
	const wallsAt = (nodeId: string) =>
		Object.values(graph.walls).filter((wall) => wall.a === nodeId || wall.b === nodeId);

	// Обход контура от узла по стенам: в каждом узле — ровно две стены
	const nodeIds: string[] = [];
	const wallIds: string[] = [];
	let current = startNodeId;
	let cameBy: string | null = null;
	for (let guard = 0; guard <= Object.keys(graph.walls).length; guard++) {
		const walls = wallsAt(current);
		if (walls.length !== 2) return false;
		const next = walls.find((wall) => wall.id !== cameBy);
		if (!next) return false;
		nodeIds.push(current);
		wallIds.push(next.id);
		cameBy = next.id;
		current = next.a === current ? next.b : next.a;
		if (current === startNodeId) break;
	}
	if (current !== startNodeId || nodeIds.length < 3) return false;

	const contour = nodeIds.map((id) => graph.nodes[id]);
	if (contour.some((node) => !node)) return false;
	// Отрицательный отступ — наружу: каждая стена отходит на свою полутолщину
	const offsets = wallIds.map((id) => -(graph.walls[id]?.thickness ?? 0) / 2);
	const moved = insetPolygon(contour, offsets);
	if (moved.length !== contour.length) return false;

	// Раздвинутый контур не должен задеть другие стены
	const loopWalls = new Set(wallIds);
	const others = {
		...graph,
		walls: Object.fromEntries(Object.entries(graph.walls).filter(([id]) => !loopWalls.has(id))),
		nodes: Object.fromEntries(Object.entries(graph.nodes).filter(([id]) => !nodeIds.includes(id))),
	};
	for (let i = 0; i < moved.length; i++) {
		const wall = graph.walls[wallIds[i]];
		const conflict = findWallConflict(others, moved[i], moved[(i + 1) % moved.length], {
			thickness: wall?.thickness ?? 0,
			nodeTolerance: 1,
			edgeTolerance: WALL_EDGE_TOLERANCE,
		});
		if (conflict) return false;
	}

	const nodes = { ...graph.nodes };
	nodeIds.forEach((id, i) => {
		nodes[id] = { ...nodes[id], x: Math.round(moved[i].x), y: Math.round(moved[i].y) };
	});
	useWallsStore.getState().restoreWalls({ ...graph, nodes });
	return true;
};
